import React from "react";
import { Typography, Tag } from "antd";
import {
  CheckCircleOutlined,
  WarningOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import type { SendcloudShipment } from "../../../types/sendcloud";

const { Text, Link } = Typography;

interface SendcloudSummaryProps {
  shipments: SendcloudShipment[];
  proposedPallets?: number;
  proposedCartons?: number;
}

/** Prefer stored tracking_url; for TransMission fall back to public T&T URL. */
function resolveTrackAndTraceUrl(shipment: SendcloudShipment): string | null {
  if (shipment.tracking_url) return shipment.tracking_url;

  const carrier = (shipment.carrier_name || "").toLowerCase();
  const method = (shipment.shipping_method || "").toLowerCase();
  const isTransmission =
    carrier.includes("transmission") || method.includes("transmission");
  const tracking = shipment.tracking_number?.trim();
  const postal = shipment.to_postal_code?.replace(/\s+/g, "").toUpperCase();

  if (isTransmission && tracking && postal) {
    return `https://api.trans-mission.nl/tt/${encodeURIComponent(tracking)}/${encodeURIComponent(postal)}`;
  }

  return null;
}

const SendcloudSummary: React.FC<SendcloudSummaryProps> = ({
  shipments,
  proposedPallets,
  proposedCartons,
}) => {
  const activeShipments = shipments.filter((s) => s.status !== "cancelled");
  if (activeShipments.length === 0) return null;

  const totalParcels = activeShipments.length;
  const totalWeightGrams = activeShipments.reduce(
    (sum, s) => sum + (s.weight || 0),
    0
  );
  const totalWeightKg = totalWeightGrams / 1000;
  const carrier = activeShipments[0].carrier_name || "Unknown";
  const shippingMethod = activeShipments[0].shipping_method || null;
  const destination = `${activeShipments[0].to_city}, ${activeShipments[0].to_country}`;

  const multiColloContext = activeShipments[0].sendcloud_data as Record<
    string,
    unknown
  > | null;
  const scTotalCartons = (
    multiColloContext?.multi_parcel_context as Record<string, unknown>
  )?.total_cartons as number | undefined;

  const statuses = [...new Set(activeShipments.map((s) => s.status))];
  const allDelivered = statuses.length === 1 && statuses[0] === "delivered";
  const hasException = statuses.includes("exception");

  const isPalletCarrier =
    carrier.toLowerCase().includes("transmission") ||
    carrier.toLowerCase() === "efreight" ||
    (shippingMethod?.toLowerCase().includes("transmission") ?? false) ||
    shippingMethod === "Own Delivery";

  const parcelsMatch =
    proposedCartons !== undefined && scTotalCartons !== undefined
      ? scTotalCartons === proposedCartons
      : proposedPallets !== undefined
        ? totalParcels === proposedPallets
        : null;

  // One T&T link per unique URL (multicollo often shares the same tracking)
  const trackAndTraceUrls = [
    ...new Set(
      activeShipments
        .map(resolveTrackAndTraceUrl)
        .filter((url): url is string => Boolean(url)),
    ),
  ];

  return (
    <div
      style={{
        marginBottom: 8,
        padding: "6px 10px",
        background: "#f6ffed",
        border: "1px solid #b7eb8f",
        borderRadius: 6,
        fontSize: 12,
      }}
    >
      <Text strong style={{ fontSize: 12, color: "#389e0d" }}>
        {carrier.toLowerCase() === "efreight" ? "Own Delivery" : "Sendcloud"}
      </Text>
      <Tag color="purple" style={{ marginLeft: 6, fontSize: 11, fontWeight: 600 }}>
        {carrier}{shippingMethod ? ` — ${shippingMethod}` : ""}
      </Tag>
      <span style={{ marginLeft: 4 }}>
        {(() => {
          const unit = isPalletCarrier ? "pallet" : "parcel";
          return `${totalParcels} ${unit}${totalParcels !== 1 ? "s" : ""}`;
        })()}
        {scTotalCartons && ` (${scTotalCartons} cartons)`}
        {" · "}
        {totalWeightKg.toFixed(1)} kg
        {" → "}
        {destination}
      </span>

      {trackAndTraceUrls.map((url, index) => (
        <Link
          key={url}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ marginLeft: 8, fontSize: 12 }}
        >
          <LinkOutlined /> Track & Trace
          {trackAndTraceUrls.length > 1 ? ` ${index + 1}` : ""}
        </Link>
      ))}

      {statuses.map((s) => (
        <Tag
          key={s}
          color={allDelivered ? "green" : hasException ? "red" : "blue"}
          style={{ marginLeft: 6, fontSize: 10 }}
        >
          {s.replace(/_/g, " ")}
        </Tag>
      ))}

      {parcelsMatch !== null && (
        <span style={{ marginLeft: 8 }}>
          {parcelsMatch ? (
            <Text style={{ fontSize: 11, color: "#52c41a" }}>
              <CheckCircleOutlined /> Matches LMD calculation
            </Text>
          ) : (
            <Text style={{ fontSize: 11, color: "#fa8c16" }}>
              <WarningOutlined /> Differs from LMD calculation
              {proposedPallets !== undefined &&
                ` (${proposedPallets} pallets proposed)`}
            </Text>
          )}
        </span>
      )}
    </div>
  );
};

export default SendcloudSummary;
